import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { postApi } from '../../../Services/apiService';

const ConvertOutHistoryModal = ({ isOpen, onClose, rawProductId, finishedProductId, executionDate }) => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'individual'

  useEffect(() => {
    if (isOpen && rawProductId && finishedProductId && executionDate) {
      fetchHistories();
    }
  }, [isOpen, rawProductId, finishedProductId, executionDate]);

  const fetchHistories = async () => {
    setLoading(true);
    try {
      // Determine if this is a group (array) or individual (string)
      const isGroup = Array.isArray(rawProductId);
      const endpoint = isGroup 
        ? '/prepack-manual-convert-out/group-histories'
        : '/prepack-manual-convert-out/histories';
      
      const payload = isGroup
        ? {
            raw_product_ids: rawProductId,
            finished_product_id: finishedProductId,
            start_date: executionDate,
            end_date: executionDate,
          }
        : {
            raw_product_id: rawProductId,
            finished_product_id: finishedProductId,
            start_date: executionDate,
            end_date: executionDate,
          };

      const response = await postApi(endpoint, payload);

      if (response.data?.records) {
        setHistories(response.data.records);
      }
    } catch (error) {
      console.error('Error fetching histories:', error);
      toast.error('Failed to fetch histories');
    } finally {
      setLoading(false);
    }
  };

  // Aggregate changes by date for grouped view
  const getGroupedChanges = () => {
    // 1. Flatten all history entries from all product records
    const allHistoryEntries = [];
    histories.forEach((record) => {
      if (record.histories && record.histories.length > 0) {
        record.histories.forEach((history) => {
          allHistoryEntries.push({
            ...history,
            raw_product_id: record.raw_product_id,
            raw_product_code: record.raw_product?.code,
            date: record.date, // execution date
            finished_product: record.finished_product,
          });
        });
      }
    });

    // 2. Process each date to calculate transitions
    const result = [];
    const entriesByDate = {};
    allHistoryEntries.forEach((entry) => {
      if (!entriesByDate[entry.date]) entriesByDate[entry.date] = [];
      entriesByDate[entry.date].push(entry);
    });

    Object.keys(entriesByDate).forEach((execDate) => {
      const dayEntries = entriesByDate[execDate].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      // Track current values for all products in the group for this specific date
      const productValues = {};
      const initialized = new Set(); // Track which products have been initialized from logs

      if (Array.isArray(rawProductId)) {
        rawProductId.forEach((id) => (productValues[id] = 0));
      }

      // Initial state: the old_value of the VERY FIRST history record for each product on this date
      dayEntries.forEach((e) => {
        if (!initialized.has(e.raw_product_id)) {
          productValues[e.raw_product_id] = parseInt(e.old_value) || 0;
          initialized.add(e.raw_product_id);
        }
      });

      let currentTotal = Object.values(productValues).reduce((sum, val) => sum + val, 0);

      dayEntries.forEach((e) => {
        const oldTotal = currentTotal;
        productValues[e.raw_product_id] = parseInt(e.new_value) || 0;
        currentTotal = Object.values(productValues).reduce((sum, val) => sum + val, 0);

        if (oldTotal !== currentTotal) {
          result.push({
            date: execDate,
            old_total: oldTotal,
            new_total: currentTotal,
            finished_product: e.finished_product,
            user: e.user,
            created_at: e.created_at,
            raw_product_code: e.raw_product_code,
          });
        }
      });
    });

    return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const isGroup = Array.isArray(rawProductId);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay custom-overlay product-quantity-add convert-out">
      <div className="modal-content custom-modal add-custom-stock multiple-reserve">
        <div className="modal-header">
          <h3>Convert Out History</h3>
          <img
            src={window.getPublicUrl('/assets/images/close.svg')}
            onClick={onClose}
            className="close"
            alt="Close"
          />
        </div>
        <div className="modal-body">
          {/* Toggle for groups only - Commented out as requested */}
          {/* {isGroup && histories.length > 0 && (
            <div className="mb-3 d-flex justify-content-end">
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${viewMode === 'grouped' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('grouped')}
                >
                  Grouped View
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'individual' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('individual')}
                >
                  Individual View
                </button>
              </div>
            </div>
          )} */}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : histories.length === 0 ? (
            <p className="text-center py-4">No history records found</p>
          ) : viewMode === 'grouped' && isGroup ? (
            // Grouped View
            <table className="table table-bordered border-top border-dark">
              <thead>
                <tr>
                  {/* <th style={{ width: '120px' }}>Date</th>
                  <th>Finished Product</th> */}
                  <th className="text-center">Convert out</th>
                  <th>Added By</th>
                  <th>Added/Edited at</th>
                </tr>
              </thead>
              <tbody>
                {getGroupedChanges().map((change, index) => (
                  <tr key={index}>
                    {/* <td>{change.date}</td>
                    <td>
                      <div>{change.finished_product?.code}</div>
                      <small className="text-muted">{change.finished_product?.name_tc}</small>
                    </td> */}
                    <td className="text-center">
                      {change.old_total !== null && change.old_total !== 0 ? (
                        <span>
                          <span>{change.old_total}</span>
                          <span className="mx-2">→</span>
                          <span>{change.new_total}</span>
                        </span>
                      ) : (
                        <span>{change.new_total}</span>
                      )}
                    </td>
                    <td>{change.user?.name || 'N/A'}</td>
                    <td>
                      <small>{format(new Date(change.created_at), 'dd MMM yyyy, h:mm a')}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="table table-bordered border-top border-dark">
              <thead>
                <tr>
                  {/* <th>Date</th>
                  <th>Raw Product</th>
                  <th>Finished Product</th> */}
                  <th className="text-center">Convert out</th>
                  <th>Added By</th>
                  <th>Added/Edited at</th>
                </tr>
              </thead>
              <tbody>
                {histories.flatMap((record) =>
                  record.histories && record.histories.length > 0
                    ? record.histories.map((history) => (
                        <tr key={history.id}>
                          {/* <td>{record.date}</td>
                          <td>
                            <div>{record.raw_product?.code}</div>
                            <small className="text-muted">{record.raw_product?.name_tc}</small>
                          </td>
                          <td>
                            <div>{record.finished_product?.code}</div>
                            <small className="text-muted">{record.finished_product?.name_tc}</small>
                          </td> */}
                          <td className="text-center">
                            {history.old_value !== null && history.old_value !== "" && history.old_value !== 0 ? (
                              <span>
                                <span >{history.old_total || history.old_value}</span>
                                <span className="mx-2">→</span>
                                <span >{history.new_total || history.new_value}</span>
                              </span>
                            ) : (
                              <span >{history.new_total || history.new_value}</span>
                            )}
                          </td>
                          <td>{history.user?.name || 'N/A'}</td>
                          <td>
                            <small>{format(new Date(history.created_at), 'dd MMM yyyy, h:mm a')}</small>
                          </td>
                        </tr>
                      ))
                    : (
                        <tr key={record.id}>
                          <td>{record.date}</td>
                          <td>
                            <div>{record.raw_product?.code}</div>
                            <small className="text-muted">{record.raw_product?.name_tc}</small>
                          </td>
                          <td>
                            <div>{record.finished_product?.code}</div>
                            <small className="text-muted">{record.finished_product?.name_tc}</small>
                          </td>
                          <td colSpan="4" className="text-center text-muted">
                            <em>No changes recorded</em>
                          </td>
                        </tr>
                      )
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="save mt-0 float-end" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertOutHistoryModal;
